/**
 * PregnancyProfile Page
 *
 * Dedicated page for creating and editing pregnancy profiles.
 * Separated from BabyProfile to avoid conditional rendering issues.
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { babyApi, inviteApi, photoApi } from '../services/api';
import {
  generateAESKey,
  encryptBabyProfile,
  decryptBabyProfile,
  getBabyKey,
  storeBabyKey,
  encryptKeyWithPublicKey,
  importPublicKey,
  exportAESKey,
  encryptBinary,
  createDecryptedImageUrl,
  encryptBabyKeyForInvite,
} from '../crypto';
import { useAuth } from '../hooks/useAuth';
import type { BabyProfileData } from '../crypto';

interface Member {
  id: string;
  name: string;
  email: string;
  role: string;
}

export default function PregnancyProfile() {
  const { babyId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isNew = !babyId;

  // Form state
  const [dueDate, setDueDate] = useState('');
  const [prePregnancyWeight, setPrePregnancyWeight] = useState('');
  const [prePregnancyWeightUnit, setPrePregnancyWeightUnit] = useState<'kg' | 'lbs'>('kg');
  const [obName, setObName] = useState('');
  const [obPhone, setObPhone] = useState('');
  const [obAddress, setObAddress] = useState('');
  const [bloodType, setBloodType] = useState('');
  const [allergies, setAllergies] = useState('');
  const [medications, setMedications] = useState('');
  const [notes, setNotes] = useState('');

  // Conversion state
  const [showConvertForm, setShowConvertForm] = useState(false);
  const [convertName, setConvertName] = useState('');
  const [convertBirthDate, setConvertBirthDate] = useState('');
  const [convertGender, setConvertGender] = useState<'male' | 'female' | 'other' | ''>('');
  const [convertBirthWeight, setConvertBirthWeight] = useState('');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Invite state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [isInviting, setIsInviting] = useState(false);

  // Profile photo
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  // Fetch existing profile data
  const { data: babyData, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['baby', babyId],
    queryFn: async () => {
      if (!babyId) return null;
      const response = await babyApi.getById(babyId);
      return response.data || response;
    },
    enabled: !!babyId,
  });

  // Populate form from existing data
  useEffect(() => {
    async function loadProfile() {
      if (!babyData?.encryptedData || !babyId) return;
      try {
        const aesKey = await getBabyKey(babyId);
        if (!aesKey) return;
        const decrypted = await decryptBabyProfile(babyData.encryptedData, aesKey);

        setDueDate(decrypted.dueDate || '');
        setPrePregnancyWeight(decrypted.prePregnancyWeight ? String(decrypted.prePregnancyWeight) : '');
        setPrePregnancyWeightUnit((decrypted.prePregnancyWeightUnit as 'kg' | 'lbs') || 'kg');
        setObName(decrypted.obGyn?.name || '');
        setObPhone(decrypted.obGyn?.phone || '');
        setObAddress(decrypted.obGyn?.address || '');
        setBloodType(decrypted.bloodType || '');
        setAllergies(decrypted.allergies?.join(', ') || '');
        setMedications(decrypted.medications?.join(', ') || '');
        setNotes(decrypted.notes || '');
      } catch { /* ignore */ }
    }
    loadProfile();
  }, [babyData, babyId]);

  // Fetch profile photo
  useEffect(() => {
    if (!babyId || !babyData?.profilePhoto?.id) return;
    async function loadPhoto() {
      try {
        const aesKey = await getBabyKey(babyId!);
        if (!aesKey) return;
        const { blob, mimeType } = await photoApi.downloadDirect(babyData.profilePhoto.id);
        const url = await createDecryptedImageUrl(blob, aesKey, mimeType);
        setProfilePhotoUrl(url);
      } catch { /* ignore */ }
    }
    loadPhoto();
  }, [babyData, babyId]);

  // Fetch members
  const { data: members } = useQuery({
    queryKey: ['baby-members', babyId],
    queryFn: async () => {
      if (!babyId) return [];
      const response = await babyApi.getMembers(babyId);
      return (response.data || response || []) as Member[];
    },
    enabled: !!babyId,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (data: { encryptedData: string; encryptedBabyKey: string; profileType: string }) => {
      const response = await babyApi.create(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['babies'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, encryptedData }: { id: string; encryptedData: string }) => {
      const response = await babyApi.update(id, { encryptedData });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['babies'] });
      queryClient.invalidateQueries({ queryKey: ['baby', babyId] });
    },
  });

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !babyId) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setIsUploadingPhoto(true);
    setError(null);

    try {
      const aesKey = await getBabyKey(babyId);
      if (!aesKey) throw new Error('Could not find encryption key');

      const fileBuffer = await file.arrayBuffer();
      const encryptedData = await encryptBinary(fileBuffer, aesKey);
      const exportedKey = await exportAESKey(aesKey);

      const uploadResponse = await photoApi.uploadDirect(
        babyId,
        { mimeType: file.type, encryptedKey: exportedKey },
        encryptedData
      );

      if (!uploadResponse.data?.photoId) throw new Error('Failed to upload photo');

      await babyApi.updateProfilePhoto(babyId, uploadResponse.data.photoId);
      queryClient.invalidateQueries({ queryKey: ['baby', babyId] });

      const imageUrl = URL.createObjectURL(file);
      if (profilePhotoUrl) URL.revokeObjectURL(profilePhotoUrl);
      setProfilePhotoUrl(imageUrl);
    } catch (err) {
      console.error('Failed to upload photo:', err);
      setError((err as Error).message || 'Failed to upload photo');
    } finally {
      setIsUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = async () => {
    if (!babyId) return;
    try {
      await babyApi.updateProfilePhoto(babyId, null);
      setProfilePhotoUrl(null);
      queryClient.invalidateQueries({ queryKey: ['baby', babyId] });
    } catch { /* ignore */ }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (!dueDate) {
        setError('Due date is required');
        setIsSubmitting(false);
        return;
      }

      const profileData: BabyProfileData = {
        name: 'My Pregnancy',
        profileType: 'pregnancy',
        dueDate: dueDate || undefined,
        prePregnancyWeight: prePregnancyWeight ? parseFloat(prePregnancyWeight) : undefined,
        prePregnancyWeightUnit: prePregnancyWeightUnit || undefined,
        bloodType: bloodType || undefined,
        allergies: allergies ? allergies.split(',').map(a => a.trim()).filter(Boolean) : undefined,
        medications: medications ? medications.split(',').map(m => m.trim()).filter(Boolean) : undefined,
        notes: notes || undefined,
        obGyn: (obName || obPhone || obAddress) ? {
          name: obName,
          phone: obPhone || undefined,
          address: obAddress || undefined,
        } : undefined,
      };

      if (isNew) {
        const aesKey = await generateAESKey();
        const encryptedData = await encryptBabyProfile(profileData, aesKey);
        const publicKey = await importPublicKey(user!.publicKey);
        const encryptedBabyKey = await encryptKeyWithPublicKey(aesKey, publicKey);
        const result = await createMutation.mutateAsync({
          encryptedData,
          encryptedBabyKey,
          profileType: 'PREGNANCY',
        });
        await storeBabyKey(result.id, encryptedBabyKey);
      } else {
        const aesKey = await getBabyKey(babyId!);
        if (!aesKey) { setError('Could not find encryption key'); setIsSubmitting(false); return; }
        const encryptedData = await encryptBabyProfile(profileData, aesKey);
        await updateMutation.mutateAsync({ id: babyId!, encryptedData });
      }

      setIsSubmitting(false);
      if (!isNew) {
        alert('Profile updated successfully!');
      }
      queryClient.invalidateQueries({ queryKey: ['babies'] });
      navigate('/app/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to save profile');
      setIsSubmitting(false);
    }
  };

  const handleConvert = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      const aesKey = await getBabyKey(babyId!);
      if (!aesKey) { setError('Could not find encryption key'); setIsSubmitting(false); return; }

      const profileData: BabyProfileData = {
        name: (convertName || 'My Baby').trim(),
        profileType: 'baby',
        dateOfBirth: convertBirthDate || undefined,
        gender: convertGender || undefined,
        bloodType: bloodType || undefined,
        allergies: allergies ? allergies.split(',').map(a => a.trim()).filter(Boolean) : undefined,
        medications: medications ? medications.split(',').map(m => m.trim()).filter(Boolean) : undefined,
        notes: notes || undefined,
        obGyn: undefined,
      };

      const encryptedData = await encryptBabyProfile(profileData, aesKey);
      await babyApi.update(babyId!, { encryptedData, profileType: 'BABY' });

      setIsSubmitting(false);
      queryClient.invalidateQueries({ queryKey: ['babies'] });
      queryClient.invalidateQueries({ queryKey: ['baby', babyId] });
      alert('Profile converted successfully! Your pregnancy history has been preserved.');
      navigate('/app/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to convert profile');
      setIsSubmitting(false);
    }
  };

  if (isLoadingProfile) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-2"
        >
          &larr; Back
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {isNew ? 'Add Pregnancy Profile' : 'Edit Pregnancy Profile'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          {isNew
            ? 'Create a profile to track your pregnancy journey'
            : 'Update your pregnancy information'}
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Photo */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Profile Photo</h2>
          <div className="flex items-center gap-6">
            {profilePhotoUrl ? (
              <img
                src={profilePhotoUrl}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover border-4 border-pink-100 dark:border-pink-900"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-pink-100 to-pink-200 dark:from-pink-800 dark:to-pink-900 flex items-center justify-center">
                <span className="text-4xl">🤰</span>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {isNew
                  ? 'Save the profile first, then you can add a photo'
                  : 'Upload a photo for your profile.'}
              </p>
              {!isNew && (
                <div className="flex gap-2">
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" id="photo-upload" />
                  <label htmlFor="photo-upload" className={`btn-primary btn-sm cursor-pointer ${isUploadingPhoto ? 'opacity-50 pointer-events-none' : ''}`}>
                    {isUploadingPhoto ? 'Uploading...' : profilePhotoUrl ? 'Change Photo' : 'Upload Photo'}
                  </label>
                  {profilePhotoUrl && (
                    <button type="button" onClick={handleRemovePhoto} className="btn-secondary btn-sm">Remove</button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Due Date *
              </label>
              <input
                id="dueDate"
                type="date"
                required
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="input"
              />
            </div>
            <div>
              <label htmlFor="bloodType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Blood Type
              </label>
              <select id="bloodType" value={bloodType} onChange={(e) => setBloodType(e.target.value)} className="input">
                <option value="">Select...</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
              </select>
            </div>
          </div>
        </div>

        {/* Pre-Pregnancy Weight */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Pre-Pregnancy Weight</h2>
          <div className="flex gap-2">
            <input
              type="number"
              value={prePregnancyWeight}
              onChange={(e) => setPrePregnancyWeight(e.target.value)}
              className="input flex-1"
              placeholder="e.g. 65"
              step="0.1"
              min="0"
            />
            <select
              value={prePregnancyWeightUnit}
              onChange={(e) => setPrePregnancyWeightUnit(e.target.value as 'kg' | 'lbs')}
              className="input w-24"
            >
              <option value="kg">kg</option>
              <option value="lbs">lbs</option>
            </select>
          </div>
        </div>

        {/* OB/GYN */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">OB/GYN</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Doctor's Name</label>
              <input type="text" value={obName} onChange={(e) => setObName(e.target.value)} className="input" placeholder="Dr. Smith" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
              <input type="tel" value={obPhone} onChange={(e) => setObPhone(e.target.value)} className="input" placeholder="+44 1234 567890" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address</label>
              <input type="text" value={obAddress} onChange={(e) => setObAddress(e.target.value)} className="input" placeholder="Medical Centre, London" />
            </div>
          </div>
        </div>

        {/* Medical Info */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Medical Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Allergies</label>
              <input
                type="text"
                value={allergies}
                onChange={(e) => setAllergies(e.target.value)}
                className="input"
                placeholder="Separate with commas (e.g. penicillin, latex)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Medications</label>
              <input
                type="text"
                value={medications}
                onChange={(e) => setMedications(e.target.value)}
                className="input"
                placeholder="Separate with commas (e.g. prenatal vitamins, iron)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="input"
                rows={3}
                placeholder="Any additional information..."
              />
            </div>
          </div>
        </div>

        {/* Medical Disclaimer */}
        <div className="card p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex gap-3">
            <span className="text-amber-500 text-xl flex-shrink-0">&#9888;</span>
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Not Medical Advice</p>
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                myTotTot is a tracking tool, not a medical device. Always consult your GP, midwife, or
                obstetrician for medical advice. If you experience severe pain, heavy bleeding, reduced
                fetal movement, or any concerning symptoms, seek immediate medical attention.
              </p>
            </div>
          </div>
        </div>

        {/* Conversion — existing pregnancy profiles only */}
        {!isNew && babyData?.role === 'PRIMARY' && (
          <div className="card p-6 bg-pink-50 dark:bg-pink-900/10 border border-pink-200 dark:border-pink-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <span>🎉</span> Baby Has Arrived?
              </h2>
              {!showConvertForm && (
                <button type="button" onClick={() => setShowConvertForm(true)} className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors">
                  Convert to Baby Profile
                </button>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Convert this pregnancy profile to a baby profile. Your pregnancy history will be preserved.
            </p>

            {showConvertForm && (
              <div className="space-y-4 mt-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-pink-200 dark:border-pink-700">
                <p className="text-sm font-medium text-pink-700 dark:text-pink-300">
                  This conversion cannot be undone.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Baby's Name</label>
                  <input type="text" value={convertName} onChange={(e) => setConvertName(e.target.value)} className="input" placeholder="Enter baby's name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Birth Date</label>
                  <input type="date" value={convertBirthDate} onChange={(e) => setConvertBirthDate(e.target.value)} className="input" max={new Date().toISOString().split('T')[0]} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Gender (optional)</label>
                  <select value={convertGender} onChange={(e) => setConvertGender(e.target.value as 'male' | 'female' | 'other' | '')} className="input">
                    <option value="">Prefer not to say</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Birth Weight (optional)</label>
                  <div className="flex gap-2">
                    <input type="number" value={convertBirthWeight} onChange={(e) => setConvertBirthWeight(e.target.value)} className="input flex-1" placeholder="e.g. 3.5" step="0.01" min="0" />
                    <span className="text-sm text-gray-500 dark:text-gray-400 self-center">kg</span>
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setShowConvertForm(false)} className="flex-1 py-2 px-3 rounded-lg font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">Cancel</button>
                  <button type="button" onClick={() => handleConvert()} disabled={isSubmitting} className="flex-1 py-2 px-3 rounded-lg font-medium bg-pink-600 hover:bg-pink-700 text-white disabled:opacity-50 transition-colors">
                    {isSubmitting ? 'Converting...' : 'Confirm Conversion'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Invite & sharing sections — existing profiles only */}
        {!isNew && babyData?.role === 'PRIMARY' && (
          <>
            {/* Invite Caregiver */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Caregivers</h2>
                <button type="button" onClick={() => setShowInviteForm(!showInviteForm)} className="btn-primary btn-sm">+ Invite Caregiver</button>
              </div>
              {showInviteForm && (
                <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <h3 className="font-medium text-gray-900 dark:text-white mb-3">Invite a Caregiver</h3>
                  {inviteLink ? (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Share this link with the caregiver:</p>
                      <div className="flex gap-2">
                        <input type="text" readOnly value={inviteLink} className="input flex-1 text-sm" />
                        <button type="button" onClick={() => { navigator.clipboard.writeText(inviteLink); alert('Link copied!'); }} className="btn-secondary btn-sm">Copy</button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                        <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="input" placeholder="caregiver@example.com" />
                      </div>
                      <button
                        type="button"
                        disabled={isInviting || !inviteEmail}
                        onClick={async () => {
                          setIsInviting(true);
                          try {
                            const babyAESKey = await getBabyKey(babyId!);
                            if (!babyAESKey) throw new Error('Unable to access encryption key');

                            const tokenBytes = new Uint8Array(32);
                            crypto.getRandomValues(tokenBytes);
                            const token = Array.from(tokenBytes).map(b => b.toString(16).padStart(2, '0')).join('');

                            const encryptedBabyKey = await encryptBabyKeyForInvite(babyAESKey, token);

                            const response = await inviteApi.create(babyId!, {
                              email: inviteEmail,
                              role: 'MEMBER',
                              expiresInDays: 7,
                              encryptedBabyKey,
                              token,
                            });

                            setInviteLink(`${window.location.origin}/invite/${response.data.token}`);
                          } catch (err: any) { alert(err.message || 'Failed to create invite'); }
                          setIsInviting(false);
                        }}
                        className="btn-primary"
                      >
                        {isInviting ? 'Creating...' : 'Create Invite Link'}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {members && members.length > 0 && (
                <div className="space-y-2">
                  {members.map((m: Member) => (
                    <div key={m.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{m.name || m.email}</p>
                        <p className="text-xs text-gray-500">{m.role}</p>
                      </div>
                      {babyData?.role === 'PRIMARY' && (
                        <button type="button" onClick={async () => { await babyApi.removeMember(babyId!, m.id); queryClient.invalidateQueries({ queryKey: ['baby-members', babyId] }); }} className="text-red-500 hover:text-red-700 text-sm">Remove</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Delete Profile */}
        {!isNew && babyData?.role === 'PRIMARY' && (
          <div className="card p-6 border-red-200 dark:border-red-800">
            <h2 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">Danger Zone</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Deleting this profile will remove all associated events, photos, and data. This cannot be undone.</p>
            {showDeleteConfirm ? (
              <div className="flex gap-2">
                <button type="button" onClick={async () => {
                  await babyApi.delete(babyId!);
                  queryClient.invalidateQueries({ queryKey: ['babies'] });
                  navigate('/app/dashboard');
                }} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm">Confirm Delete</button>
                <button type="button" onClick={() => setShowDeleteConfirm(false)} className="btn-secondary text-sm">Cancel</button>
              </div>
            ) : (
              <button type="button" onClick={() => setShowDeleteConfirm(true)} className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 px-4 py-2 rounded-lg text-sm">Delete Profile</button>
            )}
          </div>
        )}

        {/* Submit */}
        <div className="flex gap-3">
          <button type="submit" disabled={isSubmitting} className="btn-primary flex-1">
            {isSubmitting ? 'Saving...' : isNew ? 'Create Profile' : 'Save Changes'}
          </button>
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary">Cancel</button>
        </div>
      </form>
    </div>
  );
}
